import React, { useState, useEffect } from 'react';
import { Heart, Camera, Users, MessageCircle, Award, CheckCircle, Clock, Eye, ThumbsUp, Calendar, PenTool, Image, Video, Mic, Loader2 } from 'lucide-react';
import { thoughtsAPI, isLoggedIn } from '../config/api';
import { useNavigate } from 'react-router-dom';
import Layout from './common/Layout';
import SharingScope from './common/SharingScope';
import { getTimeOfDay, getUserFirstName } from '../utils/userUtils';

const Today = () => {
  const navigate = useNavigate();
  
  // Check if user is logged in
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }
  }, [navigate]);
  
  // State for form data
  const [currentThought, setCurrentThought] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [sharingScope, setSharingScope] = useState('single_family');
  const [sharingFamilyIds, setSharingFamilyIds] = useState([]);

  // State for API data
  const [moodOptions, setMoodOptions] = useState([]);
  const [recentThoughts, setRecentThoughts] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState({});
  const [todayPrompt, setTodayPrompt] = useState('');
  const [todayThought, setTodayThought] = useState(null);
  
  // State for UI
  const [isSharing, setIsSharing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Dynamic greeting based on current time and user name
  const timeOfDay = getTimeOfDay();
  const userName = getUserFirstName();
  const capitalizedTimeOfDay = timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1);
  
  // Debug log to see what name is being used

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load all data in parallel
      const [moods, recent, stats, prompt] = await Promise.all([
        thoughtsAPI.getMoods(),
        thoughtsAPI.getRecentThoughts(),
        thoughtsAPI.getWeeklyStats(),
        thoughtsAPI.getRandomPrompt()
      ]);
      
      setMoodOptions(moods);
      setRecentThoughts(recent);
      setWeeklyStats(stats);
      setTodayPrompt(prompt.content || "What made today special? Share a moment, a thought, or something you're grateful for.");
      
      // Get today's thought - API now returns 200 with has_thought flag
      const today = await thoughtsAPI.getTodayThought();
      setTodayThought(today); // Will be null if no thought exists
      
    } catch (error) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareThought = async () => {
    if (!currentThought.trim()) return;
    
    setIsSharing(true);
    setError(null);
    
    try {
      const thoughtData = {
        content: currentThought.trim(),
        mood_value: selectedMood,
        is_private: isPrivate,
        sharing_scope: sharingScope,
        family_ids: sharingFamilyIds,
      };
      
      const newThought = await thoughtsAPI.createThought(thoughtData);
      
      // Set today's thought to the newly created thought
      setTodayThought(newThought);
      
      // Update recent thoughts with the new thought
      setRecentThoughts(prev => [newThought, ...prev]);
      
      // Clear form
      setCurrentThought('');
      setSelectedMood('');
      setIsPrivate(false);
      
      // Show success message
      setSuccessMessage('Thought shared successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      setError('Failed to share thought. Please try again.');
      console.error('Error sharing thought:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleReaction = async (thoughtId, reactionType = 'like') => {
    try {
      await thoughtsAPI.addReaction(thoughtId, reactionType);
      
      // Update the thought in recent thoughts
      setRecentThoughts(prev => prev.map(thought => 
        thought.id === thoughtId 
          ? { 
              ...thought, 
              has_user_reacted: !thought.has_user_reacted,
              reaction_count: thought.has_user_reacted 
                ? thought.reaction_count - 1 
                : thought.reaction_count + 1
            }
          : thought
      ));
      
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleMediaUpload = async (file, mediaType) => {
    try {
      const mediaData = {
        media_type: mediaType,
        file: file,
        title: `Uploaded ${mediaType}`,
        description: `Media uploaded via Today component`
      };
      
      const media = await thoughtsAPI.uploadMedia(mediaData);
      
      // You could store the media ID to attach to a thought
      
    } catch (error) {
      console.error('Error uploading media:', error);
      setError('Failed to upload media. Please try again.');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5DC' }}>
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: '#556B2F' }} />
            <p className="text-lg" style={{ color: '#333333' }}>Loading your thoughts...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-[#F5F5DC] min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
              <p className="font-medium">{error}</p>
              <button 
                onClick={() => setError(null)}
                className="mt-2 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
              <p className="font-medium">{successMessage}</p>
            </div>
          )}

          {/* Welcome Section */}
          <div className="mb-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#333333' }}>
              Good {capitalizedTimeOfDay}, {userName}! 👋
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Take a moment to reflect on today. Every thought you share becomes part of your family's story.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Column - Today's Entry */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Today's Thought Entry */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <div className="flex items-center mb-4">
                  <Calendar className="w-6 h-6 mr-3" style={{ color: '#556B2F' }} />
                  <h3 className="text-xl font-semibold" style={{ color: '#333333' }}>Today's Thoughts</h3>
                </div>
                
                <div 
                  className="p-4 rounded-lg mb-4 border-l-4"
                  style={{ backgroundColor: 'white', borderLeftColor: '#E65100' }}
                >
                  <p className="text-lg" style={{ color: '#333333' }}>{todayPrompt}</p>
                </div>

                {/* Show today's thought if it exists, otherwise show input form */}
                {todayThought ? (
                  <div className="mb-4 p-4 bg-white rounded-lg border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{todayThought.mood?.emoji || '😊'}</span>
                        <span className="text-sm text-gray-500 font-medium">Today's Thought</span>
                        {todayThought.is_private && (
                          <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                            Private
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleReaction(todayThought.id)}
                          className={`flex items-center space-x-1 ${
                            todayThought.has_user_reacted ? 'text-orange-500' : 'text-gray-400'
                          } hover:text-orange-500 transition-colors`}
                        >
                          <ThumbsUp className="w-4 h-4" />
                          <span className="text-sm">{todayThought.reaction_count || 0}</span>
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-700 text-lg">{todayThought.content}</p>
                    {todayThought.media && todayThought.media.length > 0 && (
                      <div className="mt-3 flex space-x-2">
                        {todayThought.media.map((media) => (
                          <div key={media.id} className="text-xs text-gray-500">
                            📎 {media.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Thought Input - only show if no thought for today */
                  <div className="mb-4">
                    <textarea
                      value={currentThought}
                      onChange={(e) => setCurrentThought(e.target.value)}
                      placeholder="What's on your mind today? Share a memory, a thought, or something you're grateful for..."
                      className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:outline-none resize-none"
                      rows="6"
                      style={{ backgroundColor: 'white' }}
                      disabled={isSharing}
                    />
                  </div>
                )}

                {/* Form elements - only show if no thought for today */}
                {!todayThought && (
                  <>
                    {/* Mood Selection */}
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-2" style={{ color: '#333333' }}>How are you feeling today?</p>
                      <div className="flex flex-wrap gap-2">
                        {moodOptions.map((mood) => (
                          <button
                            key={mood.id}
                            onClick={() => setSelectedMood(mood.value)}
                            disabled={isSharing}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              selectedMood === mood.value 
                                ? 'border-orange-500 bg-orange-50' 
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            } ${isSharing ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div className="text-2xl mb-1">{mood.emoji}</div>
                            <div className="text-xs" style={{ color: '#333333' }}>{mood.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Privacy Toggle */}
                    <div className="mb-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={isPrivate}
                          onChange={(e) => setIsPrivate(e.target.checked)}
                          disabled={isSharing}
                          className="rounded"
                        />
                        <span className="text-sm" style={{ color: '#333333' }}>
                          Make this thought private (only visible to you)
                        </span>
                      </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <label className="p-3 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all cursor-pointer">
                        <Image className="w-5 h-5" style={{ color: '#556B2F' }} />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleMediaUpload(e.target.files[0], 'image')}
                          className="hidden"
                          disabled={isSharing}
                        />
                      </label>
                      <label className="p-3 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all cursor-pointer">
                        <Video className="w-5 h-5" style={{ color: '#556B2F' }} />
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => e.target.files[0] && handleMediaUpload(e.target.files[0], 'video')}
                          className="hidden"
                          disabled={isSharing}
                        />
                      </label>
                      <label className="p-3 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all cursor-pointer">
                        <Mic className="w-5 h-5" style={{ color: '#556B2F' }} />
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={(e) => e.target.files[0] && handleMediaUpload(e.target.files[0], 'audio')}
                          className="hidden"
                          disabled={isSharing}
                        />
                      </label>
                    </div>

                    {/* Sharing Scope */}
                    <SharingScope
                      value={sharingScope}
                      onChange={(scope, familyIds) => {
                        setSharingScope(scope);
                        setSharingFamilyIds(familyIds);
                      }}
                      className="mt-4"
                    />

                    {/* Share Button */}
                    <button
                      onClick={handleShareThought}
                      disabled={!currentThought.trim() || isSharing}
                      className={`w-full px-6 py-3 rounded-lg font-semibold transition-all ${
                        currentThought.trim() && !isSharing
                          ? 'hover:shadow-lg transform hover:scale-105'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      style={{ backgroundColor: '#E65100', color: 'white' }}
                    >
                      {isSharing ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Sharing...
                        </span>
                      ) : (
                        <>
                          <Heart className="w-5 h-5 inline mr-2" />
                          Share Today's Thought
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>

              {/* Quick Actions */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#333333' }}>Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button 
                    key="tell-story"
                    onClick={() => navigate('/tell-a-story')}
                    className="p-4 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all text-left"
                  >
                    <MessageCircle className="w-8 h-8 mb-2" style={{ color: '#556B2F' }} />
                    <div className="font-semibold text-lg" style={{ color: '#333333' }}>Tell a Story</div>
                    <div className="text-sm text-gray-600">Share a longer memory</div>
                  </button>
                  
                  <button key="add-photos" className="p-4 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all text-left">
                    <Camera className="w-8 h-8 mb-2" style={{ color: '#556B2F' }} />
                    <div className="font-semibold text-lg" style={{ color: '#333333' }}>Add Photos</div>
                    <div className="text-sm text-gray-600">Upload today's pictures</div>
                  </button>
                  
                  <button key="family-activity" className="p-4 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all text-left">
                    <Users className="w-8 h-8 mb-2" style={{ color: '#556B2F' }} />
                    <div className="font-semibold text-lg" style={{ color: '#333333' }}>Family Activity</div>
                    <div className="text-sm text-gray-600">See family updates</div>
                  </button>
                  
                  <button key="view-responses" className="p-4 rounded-lg border-2 border-gray-200 bg-white hover:shadow-md transition-all text-left">
                    <Eye className="w-8 h-8 mb-2" style={{ color: '#556B2F' }} />
                    <div className="font-semibold text-lg" style={{ color: '#333333' }}>View Responses</div>
                    <div className="text-sm text-gray-600">See family reactions</div>
                  </button>
                </div>
              </div>

              {/* Recent Thoughts */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#333333' }}>Recent Thoughts</h3>
                <div className="space-y-4">
                  {recentThoughts.length > 0 ? (
                    recentThoughts.map((thought, index) => (
                      <div key={thought.id} className="p-4 bg-white rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{thought.mood?.emoji || '😊'}</span>
                            <span className="text-sm text-gray-500">
                              {new Date(thought.created_at).toLocaleDateString()}
                            </span>
                            {thought.is_private && (
                              <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                                Private
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleReaction(thought.id)}
                              className={`flex items-center space-x-1 ${
                                thought.has_user_reacted ? 'text-orange-500' : 'text-gray-400'
                              } hover:text-orange-500 transition-colors`}
                            >
                              <ThumbsUp className="w-4 h-4" />
                              <span className="text-sm">{thought.reaction_count || 0}</span>
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-700">{thought.content}</p>
                        {thought.media && thought.media.length > 0 && (
                          <div className="mt-3 flex space-x-2">
                            {thought.media.map((media) => (
                              <div key={media.id} className="text-xs text-gray-500">
                                📎 {media.title}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No thoughts shared yet. Be the first to share your thoughts!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              
              {/* Today's Inspiration */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#333333' }}>Today's Inspiration</h3>
                <div className="space-y-3">
                  <div key="gratitude" className="p-3 bg-white rounded-lg cursor-pointer hover:shadow-sm transition-shadow">
                    <div className="text-sm font-medium" style={{ color: '#333333' }}>Gratitude</div>
                    <div className="text-xs text-gray-500">What are you thankful for today?</div>
                  </div>
                  <div key="small-victory" className="p-3 bg-white rounded-lg cursor-pointer hover:shadow-sm transition-shadow">
                    <div className="text-sm font-medium" style={{ color: '#333333' }}>A Small Victory</div>
                    <div className="text-xs text-gray-500">Celebrate something you accomplished</div>
                  </div>
                  <div key="family-moment" className="p-3 bg-white rounded-lg cursor-pointer hover:shadow-sm transition-shadow">
                    <div className="text-sm font-medium" style={{ color: '#333333' }}>Family Moment</div>
                    <div className="text-xs text-gray-500">Share a special family interaction</div>
                  </div>
                  <div key="memory-trigger" className="p-3 bg-white rounded-lg cursor-pointer hover:shadow-sm transition-shadow">
                    <div className="text-sm font-medium" style={{ color: '#333333' }}>Memory Trigger</div>
                    <div className="text-xs text-gray-500">What reminded you of the past today?</div>
                  </div>
                </div>
              </div>

              {/* Weekly Reflection */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <div className="flex items-center mb-4">
                  <Award className="w-6 h-6 mr-3" style={{ color: '#E65100' }} />
                  <h3 className="text-lg font-semibold" style={{ color: '#333333' }}>This Week</h3>
                </div>
                <div className="space-y-3">
                  <div key="thoughts-shared" className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold" style={{ color: '#E65100' }}>
                      {weeklyStats.thoughts_shared || 0}
                    </div>
                    <div className="text-sm text-gray-600">Thoughts Shared</div>
                  </div>
                  <div key="family-reactions" className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold" style={{ color: '#E65100' }}>
                      {weeklyStats.family_reactions || 0}
                    </div>
                    <div className="text-sm text-gray-600">Family Reactions</div>
                  </div>
                  {weeklyStats.current_streak > 0 && (
                    <div key="day-streak" className="text-center p-3 bg-white rounded-lg">
                      <div className="text-2xl font-bold" style={{ color: '#E65100' }}>
                        {weeklyStats.current_streak}
                      </div>
                      <div className="text-sm text-gray-600">Day Streak</div>
                    </div>
                  )}
                </div>
                {weeklyStats.weekly_goal_met && (
                  <div 
                    className="mt-4 p-3 rounded-lg text-center"
                    style={{ backgroundColor: '#E8F5E8', color: '#2D5016' }}
                  >
                    🎉 Great week! Your family loves hearing from you!
                  </div>
                )}
              </div>

              {/* Getting Started */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#333333' }}>Getting Started</h3>
                <div className="space-y-3">
                  <div key="first-thought" className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-gray-500 line-through">Share your first thought</span>
                  </div>
                  <div key="add-mood" className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-gray-500 line-through">Add a mood to your thought</span>
                  </div>
                  <div key="share-three-thoughts" className="flex items-center space-x-3">
                    <CheckCircle className={`w-5 h-5 ${(weeklyStats.thoughts_shared || 0) >= 3 ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className={`text-sm ${(weeklyStats.thoughts_shared || 0) >= 3 ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                      Share 3 thoughts this week
                    </span>
                  </div>
                  <div key="add-photos" className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-gray-300" />
                    <span className="text-sm text-gray-700">Add photos to your thoughts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Today; 